import type { VehicleDims } from "../dims.js";

/**
 * Handling per vehicle class: engine, gearbox, tyres, suspension, steering and aero. One table
 * so the feel is tuned in one place (docs/PHASE2_PLAN.md "Vehicles (driving)" lists the targets:
 * a compact does 0-100 km/h in about 11 s, a sports car in about 5 s, the bus in about 25 s).
 * The catalog's `accel` (1..10) and `topSpeedKmh` scale the engine within the class.
 */

export type Drive = "fwd" | "rwd" | "awd";

export interface Tune {
  /** Engine torque peak (N m), where it peaks, idle, the redline and the limiter (rpm). */
  torqueNm: number;
  peakRpm: number;
  idleRpm: number;
  redline: number;
  limiter: number;
  /** Forward ratios (1st..5th), reverse, final drive, driveline efficiency. */
  gears: number[];
  reverse: number;
  final: number;
  efficiency: number;
  /** Seconds a shift cuts the drive. */
  shiftTime: number;
  drive: Drive;
  /** Peak tyre friction (asphalt) and the lateral stiffness (Pacejka B), and how much the handbrake keeps at the rear. */
  grip: number;
  cornering: number;
  handbrakeGrip: number;
  /** Brake force at full pedal as a share of the weight (g), front share of the brakes. */
  brakeG: number;
  brakeFront: number;
  /** Suspension natural frequency (Hz), damping ratio, droop and bump travel (m). */
  springHz: number;
  damping: number;
  droop: number;
  bump: number;
  /** Anti-roll stiffness as a share of the spring rate. */
  antiRoll: number;
  /** Centre of mass height (share of the body height). */
  comHeight: number;
  /** Steering lock at a standstill (rad), the speed (m/s) at which the lock halves, wheel rate (rad/s). */
  lock: number;
  lockHalfSpeed: number;
  steerRate: number;
  /** Aero drag area Cd*A (m^2), rolling resistance. */
  cdA: number;
  rolling: number;
  /** Yaw damping that keeps the car straight at speed (stability assist, 0 = off). */
  stability: number;
}

const CAR_GEARS = [3.4, 2.05, 1.4, 1.06, 0.84];

export const TUNES: Record<string, Tune> = {
  city: {
    torqueNm: 165,
    peakRpm: 3800,
    idleRpm: 850,
    redline: 6200,
    limiter: 6500,
    gears: CAR_GEARS,
    reverse: 3.2,
    final: 3.9,
    efficiency: 0.86,
    shiftTime: 0.28,
    drive: "fwd",
    grip: 1.0,
    cornering: 9,
    handbrakeGrip: 0.42,
    brakeG: 1.0,
    brakeFront: 0.68,
    springHz: 1.65,
    damping: 0.38,
    droop: 0.12,
    bump: 0.09,
    antiRoll: 0.35,
    comHeight: 0.36,
    lock: 0.62,
    lockHalfSpeed: 20,
    steerRate: 3.2,
    cdA: 0.68,
    rolling: 0.013,
    stability: 0.6,
  },
  sedan: {
    torqueNm: 240,
    peakRpm: 4000,
    idleRpm: 800,
    redline: 6300,
    limiter: 6600,
    gears: CAR_GEARS,
    reverse: 3.2,
    final: 3.6,
    efficiency: 0.86,
    shiftTime: 0.26,
    drive: "fwd",
    grip: 1.02,
    cornering: 9,
    handbrakeGrip: 0.4,
    brakeG: 1.02,
    brakeFront: 0.66,
    springHz: 1.55,
    damping: 0.4,
    droop: 0.12,
    bump: 0.09,
    antiRoll: 0.4,
    comHeight: 0.36,
    lock: 0.6,
    lockHalfSpeed: 21,
    steerRate: 3.1,
    cdA: 0.66,
    rolling: 0.012,
    stability: 0.6,
  },
  suv: {
    torqueNm: 380,
    peakRpm: 3600,
    idleRpm: 750,
    redline: 5800,
    limiter: 6100,
    gears: CAR_GEARS,
    reverse: 3.3,
    final: 3.7,
    efficiency: 0.84,
    shiftTime: 0.3,
    drive: "awd",
    grip: 0.98,
    cornering: 8,
    handbrakeGrip: 0.45,
    brakeG: 0.95,
    brakeFront: 0.66,
    springHz: 1.35,
    damping: 0.38,
    droop: 0.15,
    bump: 0.11,
    antiRoll: 0.55,
    comHeight: 0.38,
    lock: 0.58,
    lockHalfSpeed: 19,
    steerRate: 2.9,
    cdA: 1.0,
    rolling: 0.014,
    stability: 0.7,
  },
  sport: {
    torqueNm: 520,
    peakRpm: 5200,
    idleRpm: 950,
    redline: 7800,
    limiter: 8100,
    gears: [3.0, 2.05, 1.5, 1.16, 0.92],
    reverse: 3.0,
    final: 3.55,
    efficiency: 0.88,
    shiftTime: 0.16,
    drive: "rwd",
    grip: 1.12,
    cornering: 11,
    handbrakeGrip: 0.36,
    brakeG: 1.15,
    brakeFront: 0.62,
    springHz: 1.95,
    damping: 0.42,
    droop: 0.1,
    bump: 0.07,
    antiRoll: 0.5,
    comHeight: 0.34,
    lock: 0.58,
    lockHalfSpeed: 24,
    steerRate: 3.6,
    cdA: 0.6,
    rolling: 0.012,
    stability: 0.45,
  },
  van: {
    torqueNm: 300,
    peakRpm: 3400,
    idleRpm: 750,
    redline: 5600,
    limiter: 5900,
    gears: CAR_GEARS,
    reverse: 3.3,
    final: 3.9,
    efficiency: 0.84,
    shiftTime: 0.3,
    drive: "fwd",
    grip: 0.96,
    cornering: 8,
    handbrakeGrip: 0.45,
    brakeG: 0.92,
    brakeFront: 0.66,
    springHz: 1.4,
    damping: 0.38,
    droop: 0.14,
    bump: 0.1,
    antiRoll: 0.5,
    comHeight: 0.4,
    lock: 0.6,
    lockHalfSpeed: 19,
    steerRate: 2.9,
    cdA: 1.05,
    rolling: 0.014,
    stability: 0.7,
  },
  bus: {
    torqueNm: 1700,
    peakRpm: 1500,
    idleRpm: 600,
    redline: 2500,
    limiter: 2700,
    gears: [4.2, 2.5, 1.6, 1.15, 0.85],
    reverse: 4.5,
    final: 5.2,
    efficiency: 0.84,
    shiftTime: 0.45,
    drive: "rwd",
    grip: 0.9,
    cornering: 7,
    handbrakeGrip: 0.6,
    brakeG: 0.75,
    brakeFront: 0.6,
    springHz: 1.15,
    damping: 0.4,
    droop: 0.12,
    bump: 0.1,
    antiRoll: 0.7,
    comHeight: 0.32,
    lock: 0.55,
    lockHalfSpeed: 12,
    steerRate: 2.0,
    cdA: 5.2,
    rolling: 0.009,
    stability: 0.9,
  },
  bike: {
    torqueNm: 18,
    peakRpm: 1200,
    idleRpm: 0,
    redline: 1800,
    limiter: 1900,
    gears: [2.6, 2.0, 1.6, 1.3, 1.1],
    reverse: 2.6,
    final: 2.4,
    efficiency: 0.95,
    shiftTime: 0.1,
    drive: "rwd",
    grip: 0.95,
    cornering: 9,
    handbrakeGrip: 0.6,
    brakeG: 0.7,
    brakeFront: 0.6,
    springHz: 2.2,
    damping: 0.5,
    droop: 0.06,
    bump: 0.05,
    antiRoll: 0,
    comHeight: 0.5,
    lock: 0.5,
    lockHalfSpeed: 8,
    steerRate: 3,
    cdA: 0.5,
    rolling: 0.008,
    stability: 1.2,
  },
};

/** Catalog class -> handling class. */
const CLASS: Record<string, keyof typeof TUNES> = {
  city: "city",
  sedan: "sedan",
  taxi: "sedan",
  police: "sedan",
  suv: "suv",
  sport: "sport",
  van: "van",
  bus: "bus",
  bike: "bike",
  scooter: "bike",
};

/**
 * The handling of one model: its class, its engine scaled by the catalog rating (accel 1..10 is
 * +-30 % torque around the class's middle rating) and a final drive that puts the top speed at
 * the limiter in 5th.
 */
export function tuneOf(
  cls: string,
  dims: Pick<VehicleDims, "wheelRadius">,
  spec: { accel: number; topSpeedKmh: number; class?: string },
): Tune {
  const base = TUNES[CLASS[spec.class ?? cls] ?? CLASS[cls] ?? "sedan"] ?? TUNES["sedan"]!;
  const mid = { city: 4, sedan: 5, suv: 6, sport: 9, van: 4, bus: 2, bike: 3 }[
    CLASS[spec.class ?? cls] ?? "sedan"
  ]!;
  const k = 1 + Math.max(-0.3, Math.min(0.3, (spec.accel - mid) * 0.08));
  // Top speed sits a little under the limiter in top gear (the drag decides the rest).
  const vTop = spec.topSpeedKmh / 3.6;
  const top = base.gears[base.gears.length - 1]!;
  const final = (base.limiter * 0.97 * Math.PI * 2 * dims.wheelRadius) / (60 * vTop * top);
  return { ...base, torqueNm: base.torqueNm * k, final };
}
